// 创业笔记 API 模块
import { API_BASE_URL } from '../config'

// 统一响应格式
interface ApiResponse<T> {
  code: number
  msg: string
  data: T
}

// 分类对象
export interface VentureNoteCat {
  id: number
  name: string
  description?: string
  status: number | boolean
  sort?: number
  count?: number
}

// 笔记对象
export interface VentureNote {
  id: number
  category_id: number
  title: string
  description: string
  content: string
  status: number
  sort: number
  view_count: number
  create_at: number
  update_at: number
  thumb: string
  tag_ids?: number[]
}

// 标签对象
export interface VentureNoteTag {
  id: number
  name: string
  view_count: number
  note_count?: number
}

// 笔记列表响应
export interface VentureNoteListData {
  list: VentureNote[]
  total: number
  page: number
  pageSize: number
}

// 笔记列表请求参数
export interface VentureNoteListParams {
  page?: number
  pageSize?: number
  cat_id?: number
  keyword?: string
}

// 通用请求封装
async function request<T>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  })
  return response.json()
}

// ========== 分类接口 ==========

// 获取分类列表
export function getCatList() {
  return request<VentureNoteCat[]>(`/api/venture-note/cat/list`)
}

// 获取所有分类
export function getAllCats() {
  return request<VentureNoteCat[]>('/api/venture-note/cat/all')
}

// 添加分类
export function addCat(data: Omit<VentureNoteCat, 'id'>) {
  return request<{ id: number }>('/api/venture-note/cat/add', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// 编辑分类
export function editCat(data: Partial<VentureNoteCat> & { id: number }) {
  return request<null>('/api/venture-note/cat/edit', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// 删除分类
export function deleteCat(id: number) {
  return request<null>('/api/venture-note/cat/delete', {
    method: 'POST',
    body: JSON.stringify({ id }),
  })
}

// ========== 笔记接口 ==========

// 获取笔记列表
export function getNoteList(params: VentureNoteListParams = {}) {
  const query = new URLSearchParams()
  if (params.page) query.append('page', String(params.page))
  if (params.pageSize) query.append('pageSize', String(params.pageSize))
  if (params.cat_id) query.append('cat_id', String(params.cat_id))
  if (params.keyword) query.append('keyword', params.keyword)

  return request<VentureNoteListData>(`/api/venture-note/list?${query.toString()}`)
}

// 获取笔记详情
export function getNoteDetail(id: number) {
  return request<VentureNote>(`/api/venture-note/detail?id=${id}`)
}

// 添加笔记
export function addNote(data: Partial<VentureNote>) {
  const payload: any = { ...data }
  if (data.category_id !== undefined) {
    payload.cat_id = data.category_id
    delete payload.category_id
  }
  return request<{ id: number }>('/api/venture-note/add', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// 编辑笔记
export function editNote(data: Partial<VentureNote> & { id: number }) {
  const payload: any = { ...data }
  if (data.category_id !== undefined) {
    payload.cat_id = data.category_id
    delete payload.category_id
  }
  return request<null>('/api/venture-note/edit', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// 删除笔记
export function deleteNote(id: number) {
  return request<null>('/api/venture-note/delete', {
    method: 'POST',
    body: JSON.stringify({ id }),
  })
}

// ========== 标签接口 ==========

// 获取标签列表
export function getTagList() {
  return request<VentureNoteTag[]>('/api/venture-note/tag/list')
}

// 添加标签
export function addTag(name: string) {
  return request<{ id: number }>('/api/venture-note/tag/add', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

// 编辑标签
export function editTag(id: number, name: string) {
  return request<null>('/api/venture-note/tag/edit', {
    method: 'POST',
    body: JSON.stringify({ id, name }),
  })
}

// 删除标签
export function deleteTag(id: number) {
  return request<null>('/api/venture-note/tag/delete', {
    method: 'POST',
    body: JSON.stringify({ id }),
  })
}

// 绑定笔记标签
export function bindTags(note_id: number, tag_ids: number[]) {
  return request<null>('/api/venture-note/tag/bind', {
    method: 'POST',
    body: JSON.stringify({ note_id, tag_ids }),
  })
}

// 解绑笔记标签
export function unbindTags(note_id: number, tag_id?: number) {
  const data: { note_id: number; tag_id?: number } = { note_id }
  if (tag_id !== undefined) data.tag_id = tag_id
  return request<null>('/api/venture-note/tag/unbind', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}
